import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import CommonButton from "./CommonButton";
import ViewSkeleton from "./ViewSkeleton";
import { useForm, Controller, useWatch } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "./CommonForm";
import { validations } from "../../validations/FormValidations";
import styles from "../../modules/Clinical/CheckinCheckout/css/checkincheckout.module.scss";

/* ─────────────────────────────────────────────────────────────
   TYPES
 ───────────────────────────────────────────────────────────── */
export interface CheckOutForm {
  checkoutDate: string;
  checkoutTime: string;
  remarks: string;
  qa_json: { questionid: number; answeroptionid: number }[];
  [key: string]: any;
}

export interface CheckOutSubject {
  id: number;
  subjectNo: string;
  subjectName: string;
  initials: string;
  checkinDate: string | null;
  checkinTime: string | null;
}

interface CheckOutModalProps {
  isOpen: boolean;
  subject: CheckOutSubject | null;
  onConfirm: (form: CheckOutForm) => void;
  onClose: () => void;
  isCheckoutLoading: boolean;
  isChecklistLoading?: boolean;
  questionlist: any[];
  checkoutDate?: string | null;
}

/* ─────────────────────────────────────────────────────────────
   COMPONENT
 ───────────────────────────────────────────────────────────── */
const CheckOutModal: React.FC<CheckOutModalProps> = ({
  isOpen,
  subject,
  onConfirm,
  onClose,
  isCheckoutLoading,
  isChecklistLoading = false,
  questionlist,
  checkoutDate,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<CheckOutForm>({
    defaultValues: {
      checkoutDate: "",
      checkoutTime: "",
      remarks: "",
    },
  });

  const watchedCheckoutDate = watch("checkoutDate"); // 👈 declared here, before fields
  const watchedRemarks = watch("remarks");

  useEffect(() => {
    if (isOpen) {
      const nowTime = new Date().toTimeString().slice(0, 5);
      const initialValues: any = {
        checkoutDate: checkoutDate ? checkoutDate.split("T")[0] : "",
        checkoutTime: nowTime,
        remarks: "",
      };
      reset(initialValues);
    }
  }, [isOpen, reset, checkoutDate]);

  if (!isOpen || !subject) return null;

  const fields: FormFieldConfig[] = [
    {
      name: "checkoutDate",
      label: "Check-Out Date",
      type: "date",
      required: true,
      placeholder: "dd-mm-yyyy",
      disabled: true,
      optionsProps: {
        minDate: (() => {
          if (!subject.checkinDate) return undefined;
          const d = new Date(subject.checkinDate.split("T")[0] + "T00:00:00");
          d.setDate(d.getDate() + 1); // 👈 adds 1 day, so if checkin is 24, min becomes 25
          return d;
        })(),
      },
      rules: {
        required: "Check-Out Date is required",
      },
    },
    {
      name: "checkoutTime",
      label: "Check-Out Time",
      type: "time",
      required: true,
      placeholder: "00:00",
      rules: {
        required: "Check-Out Time is required",
        validate: (value: string) => {
          if (
            watchedCheckoutDate &&
            subject.checkinDate &&
            watchedCheckoutDate === subject.checkinDate &&
            subject.checkinTime &&
            value < subject.checkinTime
          ) {
            return `Check-Out Time cannot be before Check-In Time (${subject.checkinTime})`;
          }
          return true;
        },
      },
    },
  ];

  const onSubmit = (data: any) => {
    if (data.remarks && data.remarks.length > 255) {
      return;
    }
    onConfirm({ ...data });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={onClose} className="p-3">
        Subject Check-Out ({subject.subjectNo})
      </ModalHeader>

      <ModalBody className="px-4">
        <CommonForm
          fields={fields}
          control={control}
          errors={errors}
          columns={2}
          setValue={setValue}
        />

        <Row className="mb-2 mt-3">
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <Label className="fw-semibold mb-0">Remarks</Label>
              <span className={`fs-12 ${ (watchedRemarks || "").length >= 255 ? "text-danger fw-bold" : "text-muted" }`}>
                {(watchedRemarks || "").length}/255
              </span>
            </div>
            <Controller
              name="remarks"
              control={control}
              rules={{
                maxLength: {
                  value: 255,
                  message: "Remarks cannot exceed 255 characters",
                },
                ...(validations.checkout?.remarks || {}),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="textarea"
                  maxLength={255}
                  placeholder="Add any additional observations..."
                  rows={3}
                  className={errors.remarks ? "is-invalid" : ""}
                />
              )}
            />
            {errors.remarks && (
              <div className="text-danger mt-1 fs-12">
                {errors.remarks.message}
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <CommonButton color="dark" outline onClick={onClose} className="px-4">
          Cancel
        </CommonButton>
        <CommonButton
          color="primary"
          outline
          onClick={handleSubmit(onSubmit)}
          disabled={isCheckoutLoading}
          className="px-4"
        >
          {isCheckoutLoading && <Spinner size="sm" className="me-2" />}
          Save
        </CommonButton>
      </ModalFooter>
    </Modal>
  );
};

export default CheckOutModal;
